import { Link } from 'react-router-dom'
import { ThemeProvider } from '@/lib/theme'
import { toLocale, useI18n } from '@/lib/i18n'
import Header from '@/components/layout/Header'
import Footer from '@/components/layout/Footer'
import JsonLd from '@/components/seo/JsonLd'
import NisgCheck from '@/components/ui/NisgCheck'
import { useDocumentMeta } from '@/lib/useDocumentMeta'
import { PAGES, SITE_URL, breadcrumbSchema, urlFor, type Locale } from '@/lib/seo'
import { contactStore } from '@/lib/variables'
import '@/styles/imprint.css'
import '@/styles/nisg.css'

/**
 * /nisg-2026 — the cybersecurity offer as a real, indexable page: what the
 * NISG 2026 is, who it covers, the deadlines, the duties, the fines, and the
 * check-and-implementation service. Prerendered in both languages like every
 * PAGES entry, with Service, FAQPage and breadcrumb JSON-LD. The announcement
 * (components/ui/Announcement) and the services list link here.
 */

export const NISG_PATH = '/nisg-2026'

type Deadline = { label: string; date: string; note: string }
type Step = { title: string; text: string }
type Faq = { q: string; a: string }
type Copy = {
  eyebrow: string
  title: string
  lede: string
  cta: string
  deadlinesTitle: string
  deadlines: Deadline[]
  whatTitle: string
  what: string[]
  whoTitle: string
  whoIntro: string
  sectorsEssential: string
  sectorsImportant: string
  whoOutro: string
  dutiesTitle: string
  dutiesIntro: string
  duties: string[]
  finesTitle: string
  fines: string[]
  serviceTitle: string
  serviceIntro: string
  steps: Step[]
  whyTitle: string
  why: string[]
  faqTitle: string
  faq: Faq[]
  ctaTitle: string
  ctaText: string
  ctaButton: string
  disclaimer: string
  sourcesTitle: string
  sources: { label: string; href: string }[]
}

const SOURCES = {
  ris: 'https://www.ris.bka.gv.at/',
  usp: 'https://www.usp.gv.at/',
  wko: 'https://www.wko.at/internetrecht/cybersicherheit-nis-2-richtlinie',
}

const COPY: Record<Locale, Copy> = {
  de: {
    eyebrow: 'Leistung · NISG 2026',
    title: 'NISG 2026: Cybersicherheit ist jetzt Pflicht. Wir machen Ihr Unternehmen bereit.',
    lede: 'Seit 1. Oktober 2026 gilt in Österreich das Netz- und Informationssystemsicherheitsgesetz 2026 (NISG 2026), die Umsetzung der EU-Richtlinie NIS 2. Softmaze prüft, ob und wie das Gesetz Sie trifft, misst Ihren Ist-Zustand an den Anforderungen und setzt um, was fehlt: technisch, in Ihren Prozessen und in der Dokumentation.',
    cta: 'NISG-Check anfragen',
    deadlinesTitle: 'Die Fristen',
    deadlines: [
      { label: 'In Kraft seit', date: '1. Oktober 2026', note: 'Die Pflichten zum Risikomanagement und zur Meldung von Vorfällen gelten seit diesem Tag, ohne Übergangsfrist.' },
      { label: 'Registrierung bis', date: '31. Dezember 2026', note: 'Wesentliche und wichtige Einrichtungen registrieren sich binnen drei Monaten beim Bundesamt für Cybersicherheit über das Unternehmensserviceportal (USP).' },
      { label: 'Selbstdeklaration bis', date: '30. September 2027', note: 'Binnen zwölf Monaten legen registrierte Einrichtungen dar, welche Risikomanagement-Maßnahmen sie umgesetzt haben.' },
    ],
    whatTitle: 'Was ist das NISG 2026?',
    what: [
      'Das NISG 2026 (BGBl. I Nr. 94/2025) ersetzt das NISG 2018 und setzt die EU-Richtlinie NIS 2 in österreichisches Recht um. Es verpflichtet Unternehmen und Organisationen in sicherheitsrelevanten Sektoren, ihre Netz- und Informationssysteme nach dem Stand der Technik zu schützen, Sicherheitsvorfälle zu melden und die Verantwortung dafür in der Geschäftsführung zu verankern.',
      'Zuständig ist das neue Bundesamt für Cybersicherheit. Es führt das Register der betroffenen Einrichtungen, nimmt Meldungen entgegen, prüft die Umsetzung und verhängt Geldbußen.',
    ],
    whoTitle: 'Wer ist betroffen?',
    whoIntro: 'Betroffen sind in der Regel mittlere und große Unternehmen (ab 50 Mitarbeitenden oder 10 Millionen Euro Jahresumsatz) in den Sektoren der Anlagen 1 und 2 des Gesetzes, eingeteilt in wesentliche und wichtige Einrichtungen:',
    sectorsEssential: 'Wesentliche Sektoren: Energie, Verkehr, Bankwesen, Finanzmarktinfrastrukturen, Gesundheitswesen, Trinkwasser, Abwasser, digitale Infrastruktur, Verwaltung von IKT-Diensten, öffentliche Verwaltung, Weltraum.',
    sectorsImportant: 'Wichtige Sektoren: Post- und Kurierdienste, Abfallbewirtschaftung, Chemie, Lebensmittel, verarbeitendes Gewerbe (etwa Medizinprodukte, Elektronik, Maschinenbau, Fahrzeugbau), digitale Dienste (Marktplätze, Suchmaschinen, soziale Netzwerke), Forschung.',
    whoOutro: 'Einzelne Anbieter, etwa von DNS-Diensten, Rechenzentren oder Vertrauensdiensten, fallen unabhängig von ihrer Größe unter das Gesetz. Und wer selbst nicht betroffen ist, wird oft von betroffenen Kunden als Lieferant in die Pflicht genommen: NIS 2 verlangt Sicherheit in der Lieferkette. Ob Sie betroffen sind, muss jedes Unternehmen selbst prüfen; genau damit beginnt unser Check.',
    dutiesTitle: 'Was Unternehmen jetzt tun müssen',
    dutiesIntro: 'Das Gesetz verlangt ein dokumentiertes Risikomanagement mit geeigneten, verhältnismäßigen Maßnahmen. Die Mindestliste entspricht den zehn Bereichen der NIS-2-Richtlinie:',
    duties: [
      'Risikoanalyse und Sicherheitskonzepte für die Informationssysteme',
      'Bewältigung von Sicherheitsvorfällen und ein Meldeprozess: Frühwarnung binnen 24 Stunden, Meldung binnen 72 Stunden, Abschlussbericht binnen eines Monats',
      'Aufrechterhaltung des Betriebs: Backups, Wiederherstellung, Krisenmanagement',
      'Sicherheit der Lieferkette, einschließlich der Beziehungen zu Dienstleistern',
      'Sicherheit bei Erwerb, Entwicklung und Wartung von Systemen, inklusive Umgang mit Schwachstellen',
      'Verfahren, um die Wirksamkeit der Maßnahmen zu bewerten',
      'Cyberhygiene und Schulungen, auch für die Geschäftsführung',
      'Einsatz von Kryptografie und, wo angebracht, Verschlüsselung',
      'Personalsicherheit, Zugriffskontrolle und Verwaltung der Anlagen',
      'Mehrfaktor-Authentifizierung, gesicherte Kommunikation und Notfallkommunikation',
    ],
    finesTitle: 'Strafen und Haftung',
    fines: [
      'Wesentliche Einrichtungen: Geldbußen bis zu 10 Millionen Euro oder 2 % des weltweiten Jahresumsatzes, je nachdem, welcher Betrag höher ist.',
      'Wichtige Einrichtungen: bis zu 7 Millionen Euro oder 1,4 % des weltweiten Jahresumsatzes.',
      'Die Leitungsorgane müssen die Maßnahmen genehmigen und überwachen, an Schulungen teilnehmen und können für Verstöße persönlich verantwortlich gemacht werden.',
    ],
    serviceTitle: 'Unser NISG-Check und die Umsetzung',
    serviceIntro: 'Wir sind ein Software-Studio, kein Papierlieferant: Wir bauen die Systeme, die abgesichert werden müssen, und wissen, wo die Lücken üblicherweise sind. Der Ablauf:',
    steps: [
      { title: '1. Betroffenheitsanalyse', text: 'Wesentliche, wichtige oder keine Einrichtung? Wir prüfen Sektor, Größe und Sonderfälle und halten das Ergebnis schriftlich fest, auch für Ihre Kunden und Versicherer.' },
      { title: '2. Gap-Analyse', text: 'Ihr Ist-Zustand gegenüber den Risikomanagement-Maßnahmen des NISG 2026: Technik, Prozesse, Dokumentation, Lieferanten. Sie bekommen eine priorisierte Liste mit Aufwand und Budget.' },
      { title: '3. Umsetzung', text: 'Zugriffskontrolle und Mehrfaktor-Authentifizierung, Backups und Wiederherstellung, Verschlüsselung, Monitoring und Patching, Incident-Response-Plan und Meldeprozess, Schulung für Geschäftsführung und Teams.' },
      { title: '4. Registrierung und Nachweis', text: 'Registrierung im USP, Vorbereitung der Selbstdeklaration und ein Maßnahmenregister, das Sie danach selbst aktuell halten können.' },
    ],
    whyTitle: 'Warum Softmaze',
    why: [
      'Ein Team für Software und Sicherheit: Wir setzen die Maßnahmen in den Systemen um, die wir auch bauen und betreiben.',
      'Verhältnismäßig: Das Gesetz verlangt angemessene Maßnahmen, keine Zertifikate. Wir planen, was zu Ihrer Größe passt.',
      'In Wien, auf Deutsch und Englisch, mit festen Ansprechpersonen.',
    ],
    faqTitle: 'Häufige Fragen zum NISG 2026',
    faq: [
      { q: 'Seit wann gilt das NISG 2026?', a: 'Seit 1. Oktober 2026. Die Pflichten zum Risikomanagement und zur Meldung von Sicherheitsvorfällen gelten seit diesem Tag ohne Übergangsfrist.' },
      { q: 'Bis wann muss sich mein Unternehmen registrieren?', a: 'Bis 31. Dezember 2026 beim Bundesamt für Cybersicherheit über das Unternehmensserviceportal (USP). Wer später unter das Gesetz fällt, hat ab diesem Zeitpunkt drei Monate Zeit.' },
      { q: 'Was ist die Selbstdeklaration?', a: 'Registrierte Einrichtungen legen bis 30. September 2027 dar, welche Risikomanagement-Maßnahmen sie umgesetzt haben. Dafür brauchen Sie eine belastbare Dokumentation.' },
      { q: 'Ist mein Unternehmen mit weniger als 50 Mitarbeitenden betroffen?', a: 'Meist nicht direkt. Ausnahmen sind bestimmte Anbieter, die unabhängig von der Größe erfasst sind, und die Lieferkette: Betroffene Kunden müssen die Sicherheit ihrer Lieferanten prüfen und geben Anforderungen weiter.' },
      { q: 'Welche Strafen drohen?', a: 'Bis zu 10 Millionen Euro oder 2 % des weltweiten Jahresumsatzes für wesentliche Einrichtungen, bis zu 7 Millionen Euro oder 1,4 % für wichtige. Die Geschäftsführung kann persönlich zur Verantwortung gezogen werden.' },
      { q: 'Wie lange dauert der NISG-Check?', a: 'Betroffenheits- und Gap-Analyse dauern bei einem mittleren Unternehmen typischerweise zwei bis drei Wochen. Die Umsetzung hängt von den Lücken ab; wir planen sie so, dass Registrierung und Selbstdeklaration fristgerecht möglich sind.' },
    ],
    ctaTitle: 'Starten Sie mit dem Check.',
    ctaText: 'Erzählen Sie uns kurz, was Ihr Unternehmen macht und wie groß es ist. Wir sagen Ihnen innerhalb eines Werktags, ob das NISG 2026 für Sie gilt und wie wir vorgehen würden.',
    ctaButton: 'NISG-Check anfragen',
    disclaimer: 'Keine Rechtsberatung: Diese Seite fasst das Gesetz nach bestem Wissen zusammen. Verbindlich sind der Gesetzestext und die Auskünfte des Bundesamts für Cybersicherheit; Rechtsfragen klären wir gemeinsam mit Ihrer Anwältin oder Ihrem Anwalt.',
    sourcesTitle: 'Quellen',
    sources: [
      { label: 'NISG 2026 im Rechtsinformationssystem des Bundes (RIS)', href: SOURCES.ris },
      { label: 'Registrierung über das Unternehmensserviceportal (USP)', href: SOURCES.usp },
      { label: 'WKO: Cybersicherheit und NIS-2-Richtlinie', href: SOURCES.wko },
    ],
  },
  en: {
    eyebrow: 'Service · NISG 2026',
    title: 'NISG 2026: cybersecurity is now the law. We get your company ready.',
    lede: 'Since 1 October 2026 the Austrian Network and Information Systems Security Act 2026 (NISG 2026) applies, the implementation of the EU NIS 2 directive. Softmaze checks whether and how the law applies to you, measures your current state against its requirements and implements what is missing: technically, in your processes and in the paperwork.',
    cta: 'Request a NISG check',
    deadlinesTitle: 'The deadlines',
    deadlines: [
      { label: 'In force since', date: '1 October 2026', note: 'The risk-management and incident-reporting duties apply from this day, with no transition period.' },
      { label: 'Registration by', date: '31 December 2026', note: 'Essential and important entities register within three months with the Bundesamt für Cybersicherheit through the business service portal (USP).' },
      { label: 'Self-declaration by', date: '30 September 2027', note: 'Within twelve months, registered entities declare which risk-management measures they have implemented.' },
    ],
    whatTitle: 'What is the NISG 2026?',
    what: [
      'The NISG 2026 (Federal Law Gazette BGBl. I No. 94/2025) replaces the NISG 2018 and transposes the EU NIS 2 directive into Austrian law. It obliges companies and organisations in security-relevant sectors to protect their network and information systems to the state of the art, to report security incidents and to anchor the responsibility for this in management.',
      'The new Bundesamt für Cybersicherheit (Federal Office for Cybersecurity) is in charge. It keeps the register of covered entities, receives reports, audits implementation and imposes fines.',
    ],
    whoTitle: 'Who is covered?',
    whoIntro: 'As a rule, medium and large companies (from 50 employees or EUR 10 million annual turnover) in the sectors of annexes 1 and 2 of the act, divided into essential and important entities:',
    sectorsEssential: 'Essential sectors: energy, transport, banking, financial market infrastructure, health, drinking water, waste water, digital infrastructure, ICT service management, public administration, space.',
    sectorsImportant: 'Important sectors: postal and courier services, waste management, chemicals, food, manufacturing (such as medical devices, electronics, machinery, vehicles), digital providers (marketplaces, search engines, social networks), research.',
    whoOutro: 'Some providers, such as DNS services, data centres or trust services, are covered regardless of size. And companies that are not covered themselves are often pulled in by covered customers as suppliers: NIS 2 demands supply-chain security. Every company has to check for itself whether it is covered; that is exactly where our check starts.',
    dutiesTitle: 'What companies have to do now',
    dutiesIntro: 'The act requires documented risk management with appropriate, proportionate measures. The minimum list matches the ten areas of the NIS 2 directive:',
    duties: [
      'Risk analysis and security policies for the information systems',
      'Incident handling and a reporting process: early warning within 24 hours, notification within 72 hours, final report within one month',
      'Business continuity: backups, recovery, crisis management',
      'Supply-chain security, including relationships with service providers',
      'Security in acquiring, developing and maintaining systems, including vulnerability handling',
      'Procedures to assess the effectiveness of the measures',
      'Cyber hygiene and training, including for management',
      'Use of cryptography and, where appropriate, encryption',
      'Human-resources security, access control and asset management',
      'Multi-factor authentication, secured communication and emergency communication',
    ],
    finesTitle: 'Fines and liability',
    fines: [
      'Essential entities: fines of up to EUR 10 million or 2 % of worldwide annual turnover, whichever is higher.',
      'Important entities: up to EUR 7 million or 1.4 % of worldwide annual turnover.',
      'Management bodies must approve and oversee the measures, attend training and can be held personally responsible for violations.',
    ],
    serviceTitle: 'Our NISG check and implementation',
    serviceIntro: 'We are a software studio, not a paperwork supplier: we build the systems that have to be secured and know where the gaps usually are. How it goes:',
    steps: [
      { title: '1. Applicability check', text: 'Essential, important or not covered? We check sector, size and the special cases and put the result in writing, for your customers and insurers too.' },
      { title: '2. Gap analysis', text: 'Your current state against the NISG 2026 risk-management measures: technology, processes, documentation, suppliers. You get a prioritised list with effort and budget.' },
      { title: '3. Implementation', text: 'Access control and multi-factor authentication, backups and recovery, encryption, monitoring and patching, an incident-response plan and reporting process, training for management and teams.' },
      { title: '4. Registration and evidence', text: 'Registration in the USP, preparation of the self-declaration and a register of measures you can keep up to date yourself afterwards.' },
    ],
    whyTitle: 'Why Softmaze',
    why: [
      'One team for software and security: we implement the measures in the systems we also build and run.',
      'Proportionate: the act asks for appropriate measures, not certificates. We plan what fits your size.',
      'In Vienna, in German and English, with fixed contacts.',
    ],
    faqTitle: 'Frequently asked questions about the NISG 2026',
    faq: [
      { q: 'Since when does the NISG 2026 apply?', a: 'Since 1 October 2026. The risk-management and incident-reporting duties have applied from that day with no transition period.' },
      { q: 'By when does my company have to register?', a: 'By 31 December 2026 with the Bundesamt für Cybersicherheit through the business service portal (USP). Companies that become covered later have three months from that point.' },
      { q: 'What is the self-declaration?', a: 'By 30 September 2027 registered entities declare which risk-management measures they have implemented. You need solid documentation for that.' },
      { q: 'Is my company with fewer than 50 employees covered?', a: 'Usually not directly. The exceptions are certain providers that are covered regardless of size, and the supply chain: covered customers have to check their suppliers’ security and pass requirements on.' },
      { q: 'What are the fines?', a: 'Up to EUR 10 million or 2 % of worldwide annual turnover for essential entities, up to EUR 7 million or 1.4 % for important ones. Management can be held personally responsible.' },
      { q: 'How long does the NISG check take?', a: 'Applicability and gap analysis typically take two to three weeks for a medium-sized company. Implementation depends on the gaps; we plan it so that registration and self-declaration are possible in time.' },
    ],
    ctaTitle: 'Start with the check.',
    ctaText: 'Tell us briefly what your company does and how big it is. Within one working day we tell you whether the NISG 2026 applies to you and how we would proceed.',
    ctaButton: 'Request a NISG check',
    disclaimer: 'Not legal advice: this page summarises the act to the best of our knowledge. The text of the act and the information of the Bundesamt für Cybersicherheit are authoritative; legal questions we settle together with your lawyer.',
    sourcesTitle: 'Sources',
    sources: [
      { label: 'NISG 2026 in the Austrian legal information system (RIS)', href: SOURCES.ris },
      { label: 'Registration through the business service portal (USP)', href: SOURCES.usp },
      { label: 'WKO: cybersecurity and the NIS 2 directive', href: SOURCES.wko },
    ],
  },
}

function schemas(c: Copy, locale: Locale) {
  const url = urlFor(NISG_PATH, locale)
  const meta = PAGES[NISG_PATH][locale]
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      name: meta.title,
      url,
      description: meta.description,
      inLanguage: locale,
      isPartOf: { '@id': `${SITE_URL}/#website` },
      about: { '@id': `${SITE_URL}/#organization` },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: locale === 'de' ? 'NISG-2026-Check und Umsetzung' : 'NISG 2026 check and implementation',
      serviceType: locale === 'de' ? 'Cybersicherheit und NIS-2-Compliance' : 'Cybersecurity and NIS 2 compliance',
      description: meta.description,
      url,
      provider: { '@id': `${SITE_URL}/#organization` },
      areaServed: [
        { '@type': 'Place', name: 'Vienna' },
        { '@type': 'Country', name: 'Austria' },
      ],
      availableLanguage: ['de', 'en'],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: c.faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    breadcrumbSchema(NISG_PATH, locale),
  ].filter(Boolean) as Record<string, unknown>[]
}

export default function NisgPage() {
  const { lang, to, t } = useI18n()
  const locale = toLocale(lang)
  const c = COPY[locale]
  const prefill = () => contactStore.prefill({ subject: t('inquiry.nisgSubject'), message: t('inquiry.nisgMessage') })
  useDocumentMeta({ ...PAGES[NISG_PATH][locale], path: NISG_PATH, locale })
  return (
    <ThemeProvider>
      <Header active="about" onNavigate={() => {}} />
      <JsonLd data={schemas(c, locale)} />

      <main className="imprint-page nisg">
        <div className="container imprint-content nisg__content">
          <span className="eyebrow">{c.eyebrow}</span>
          <h1 className="imprint-title nisg__title">{c.title}</h1>
          <p className="lede">{c.lede}</p>
          <p className="nisg__hero-cta">
            <Link to={to('/contact')} className="btn btn--primary" onClick={prefill}>
              {c.cta}
              <span className="arrow" aria-hidden>
                →
              </span>
            </Link>
          </p>

          <section className="imprint-section" aria-labelledby="nisg-deadlines">
            <h2 id="nisg-deadlines">{c.deadlinesTitle}</h2>
            <dl className="nisg__deadlines">
              {c.deadlines.map((d, i) => (
                <div key={d.label} className={`nisg__deadline ${i === 1 ? 'is-next' : ''}`}>
                  <dt className="mono">{d.label}</dt>
                  <dd>
                    <strong>{d.date}</strong>
                    <span>{d.note}</span>
                  </dd>
                </div>
              ))}
            </dl>
          </section>

          <section className="imprint-section" aria-labelledby="nisg-what">
            <h2 id="nisg-what">{c.whatTitle}</h2>
            {c.what.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </section>

          <section className="imprint-section" aria-labelledby="nisg-who">
            <h2 id="nisg-who">{c.whoTitle}</h2>
            <p>{c.whoIntro}</p>
            <ul>
              <li>{c.sectorsEssential}</li>
              <li>{c.sectorsImportant}</li>
            </ul>
            <p>{c.whoOutro}</p>
            <NisgCheck ask="navigate" />
          </section>

          <section className="imprint-section" aria-labelledby="nisg-duties">
            <h2 id="nisg-duties">{c.dutiesTitle}</h2>
            <p>{c.dutiesIntro}</p>
            <ol className="nisg__duties">
              {c.duties.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ol>
          </section>

          <section className="imprint-section" aria-labelledby="nisg-fines">
            <h2 id="nisg-fines">{c.finesTitle}</h2>
            <ul>
              {c.fines.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          </section>

          <section className="imprint-section" aria-labelledby="nisg-service">
            <h2 id="nisg-service">{c.serviceTitle}</h2>
            <p>{c.serviceIntro}</p>
            <ol className="nisg__steps">
              {c.steps.map((s) => (
                <li key={s.title}>
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                </li>
              ))}
            </ol>
            <h3>{c.whyTitle}</h3>
            <ul>
              {c.why.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </section>

          <section className="imprint-section" aria-labelledby="nisg-faq">
            <h2 id="nisg-faq">{c.faqTitle}</h2>
            {c.faq.map((f) => (
              <div key={f.q} className="nisg__faq">
                <h3>{f.q}</h3>
                <p>{f.a}</p>
              </div>
            ))}
          </section>

          <section className="imprint-section nisg__cta" aria-labelledby="nisg-cta">
            <h2 id="nisg-cta">{c.ctaTitle}</h2>
            <p>{c.ctaText}</p>
            <Link to={to('/contact')} className="btn btn--primary" onClick={prefill}>
              {c.ctaButton}
              <span className="arrow" aria-hidden>
                →
              </span>
            </Link>
          </section>

          <section className="imprint-section nisg__sources">
            <p className="muted">{c.disclaimer}</p>
            <h3>{c.sourcesTitle}</h3>
            <ul>
              {c.sources.map((s) => (
                <li key={s.href}>
                  <a href={s.href} target="_blank" rel="noopener noreferrer">
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </main>

      <Footer />
    </ThemeProvider>
  )
}
